import { supabase } from './supabase'

// PO / shipment lifecycle (decided Oct 2026)
// Nothing is received in the app — stock comes from the inventory uploads on Planning Inputs.
//   PO:        Draft → Confirmed (or Cancelled). "Completed" is never stored: a confirmed PO shows as Completed
//              once every part of it has landed (see poCompleted).
//   Shipment:  Planned → Shipped (or Cancelled). "Landed" is never stored: a shipment shows as Landed once its
//              arrival date is before the latest upload for its destination.
// Older stored values are kept in the database and read as their nearest new status, so no data migration is needed.

export const PO_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cancelled', label: 'Cancelled' },
]
// legacy sent / partially_received / received → confirmed
export function poBaseStatus(status) {
  return status === 'draft' ? 'draft' : status === 'cancelled' ? 'cancelled' : 'confirmed'
}

// Stored values reuse the existing shipment statuses: scheduled = Planned, in_transit = Shipped
export const SHIPMENT_STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Planned' },
  { value: 'in_transit', label: 'Shipped' },
  { value: 'cancelled', label: 'Cancelled' },
]
export const SHIPPED_STATUSES = ['in_transit', 'arrived', 'receiving', 'received']
// created / scheduled → planned; in_transit / arrived / receiving / received → shipped
export function shipmentBaseStatus(status) {
  if (status === 'cancelled') return 'cancelled'
  if (status === 'created' || status === 'scheduled') return 'planned'
  return 'shipped'
}
export const shipmentSelectValue = status => ({ planned: 'scheduled', shipped: 'in_transit', cancelled: 'cancelled' })[shipmentBaseStatus(status)]

// What to show: planned / shipped / landed / cancelled for shipments; draft / confirmed / completed / cancelled for POs
export const DISPLAY = {
  draft: { label: 'Draft', badge: 'neutral' },
  confirmed: { label: 'Confirmed', badge: 'accent' },
  completed: { label: 'Completed', badge: 'success' },
  planned: { label: 'Planned', badge: 'neutral' },
  shipped: { label: 'Shipped', badge: 'accent' },
  landed: { label: 'Landed', badge: 'success' },
  cancelled: { label: 'Cancelled', badge: 'danger' },
}

const today = () => new Date().toISOString().slice(0, 10)

// Loads everything needed to work out which POs are Completed and which shipments have Landed for a brand.
// Returns { poState: {poId: 'draft'|'confirmed'|'completed'|'cancelled'}, shipmentState: {shipmentId: 'planned'|'shipped'|'landed'|'cancelled'}, asOf }
export async function loadOrderStates(brandId) {
  const [locs, snaps, prods, pos, ships] = await Promise.all([
    supabase.from('locations').select('id, type').eq('brand_id', brandId).eq('is_active', true),
    supabase.from('inventory_snapshots').select('location_id, as_of_date, created_at').eq('brand_id', brandId).order('created_at', { ascending: false }).limit(50),
    supabase.from('products').select('id, default_destination').eq('brand_id', brandId),
    supabase.from('purchase_orders').select('id, status, expected_date, po_line_items(id, product_id, quantity_ordered, quantity_received)').eq('brand_id', brandId).is('deleted_at', null),
    supabase.from('shipments').select('id, po_id, status, ship_to_location_id, is_direct_ship, expected_arrival, shipment_line_items(po_line_item_id, product_id, quantity_shipped, total_units, quantity_ordered, quantity_received)').eq('brand_id', brandId).is('deleted_at', null),
  ])
  const err = [locs, snaps, prods, pos, ships].find(r => r.error)
  if (err) throw err.error

  // latest upload date per location
  const asOf = {}
  ;(snaps.data || []).forEach(s => { if (!asOf[s.location_id]) asOf[s.location_id] = s.as_of_date })
  const fbaLoc = (locs.data || []).find(l => l.type === 'amazon_fba')
  const whLoc = (locs.data || []).find(l => l.type !== 'amazon_fba' && l.type !== 'supplier')
  const productById = Object.fromEntries((prods.data || []).map(p => [p.id, p]))
  const poById = Object.fromEntries((pos.data || []).map(p => [p.id, p]))

  // A shipment has landed when its arrival date is before the latest upload for its destination
  // (direct-to-customer shipments: once the arrival date has passed). Old "received" shipments count as landed.
  const shipmentState = {}
  ;(ships.data || []).forEach(s => {
    const base = shipmentBaseStatus(s.status)
    if (base === 'cancelled') { shipmentState[s.id] = 'cancelled'; return }
    const date = s.expected_arrival || poById[s.po_id]?.expected_date
    const cutoff = s.is_direct_ship ? today() : asOf[s.ship_to_location_id]
    const landed = s.status === 'received' || !!(date && cutoff && date < cutoff)
    shipmentState[s.id] = landed ? 'landed' : base
  })

  const shipsByPo = {}
  ;(ships.data || []).forEach(s => { if (s.po_id && shipmentState[s.id] !== 'cancelled') (shipsByPo[s.po_id] = shipsByPo[s.po_id] || []).push(s) })

  // A confirmed PO is Completed when every open unit has landed: each shipment it is split into has landed,
  // and any units not yet on a shipment are past the PO's expected date at their default destination.
  const poState = {}
  ;(pos.data || []).forEach(po => {
    const base = poBaseStatus(po.status)
    if (base !== 'confirmed') { poState[po.id] = base; return }
    let productLines = 0, allLanded = true
    ;(po.po_line_items || []).forEach(li => {
      if (!li.product_id) return // fee lines (setup, freight…) don't decide completion
      productLines++
      const open = Math.max(0, (Number(li.quantity_ordered) || 0) - (Number(li.quantity_received) || 0))
      if (!open) return
      let assigned = 0
      ;(shipsByPo[po.id] || []).forEach(s => {
        (s.shipment_line_items || []).filter(sl => (sl.po_line_item_id ? sl.po_line_item_id === li.id : sl.product_id && sl.product_id === li.product_id)).forEach(sl => {
          const q = Math.max(0, (Number(sl.quantity_shipped ?? sl.total_units ?? sl.quantity_ordered) || 0) - (Number(sl.quantity_received) || 0))
          if (!q) return
          assigned += q
          if (shipmentState[s.id] !== 'landed') allLanded = false
        })
      })
      if (open - assigned > 0) {
        const dest = productById[li.product_id]?.default_destination === 'fba' ? fbaLoc : whLoc
        const cutoff = dest ? asOf[dest.id] : null
        if (!(po.expected_date && cutoff && po.expected_date < cutoff)) allLanded = false
      }
    })
    // Product lines with nothing left open (older POs received in the app) count as landed;
    // a PO with no product lines yet stays Confirmed
    poState[po.id] = productLines && allLanded ? 'completed' : 'confirmed'
  })

  return { poState, shipmentState, asOf }
}
